import { useId, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { Trans, useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import { AlertList } from "@/components/ui/alert";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import type {
  Filterdescriptor,
  Filterstand,
} from "@/components/ui/data-table-filter";
import { Label } from "@/components/ui/label";
import { Pager } from "@/components/ui/pager";
import { Switch } from "@/components/ui/switch";
import { ListDetail } from "@/components/ListDetail";
import { datumTijd } from "@/i18n/datum";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import { PersoonZoeker } from "@/features/koppelingen/PersoonZoeker";
import { useMe } from "@/features/session/useMe";
import {
  useDubbeleAdressen,
  useKoppel,
  useOntkoppel,
} from "@/features/koppelingen/useKoppelingen";
import { AccountFilters } from "./AccountFilters";
import { IssueLokaalAccount } from "./IssueLokaalAccount";
import {
  accountBronLabel,
  accountDeurLabel,
  accountStatusLabel,
  DEUREN,
  STATUSSEN,
  type AccountFilter,
  useDeleteAccount,
  useLokaleAccountsPaged,
  useResetCredential,
  useRevokeInloglinks,
  useSendInloglink,
  useSetRights,
} from "./useLokaleAccounts";

type Account = NonNullable<
  ReturnType<typeof useLokaleAccountsPaged>["data"]
>["items"][number];

type Duplicaat = NonNullable<
  ReturnType<typeof useDubbeleAdressen>["data"]
>[number];

const helper = createColumnHelper<typeof dataTableFeatures, Account>();

/** `status` is derived per row, so it has no server ordering and falls back to the default. */
const SORT_BY: Record<string, string> = { displayName: "displayName" };

const datum = (
  value: string | null | undefined,
  t: TFunction<"lokaleAccounts">,
) => (value ? new Date(value).toLocaleDateString("nl-NL") : t("detail.nooit"));

const isOutstanding = (expiresOn: string | null | undefined) =>
  expiresOn ? new Date(expiresOn) > new Date() : false;

/**
 * Where this account stands with its persoon, or nothing at all when it is linked and nobody has
 * flagged it. Drift outranks the rest: a link that exists but is known to be questionable is the
 * one a beheerder has to look at twice.
 */
const koppelingLabel = (account: Account, t: TFunction<"lokaleAccounts">) =>
  account.linkDriftedOn
    ? t("lijst.koppelingAfwijkend")
    : account.persoonId
      ? null
      : t("lijst.koppelingWacht");

/** A linked account's naam and address, address under the name rather than beside it: as its
 *  own column it would push the list pane past its half of an iPad. */
function GekoppeldNaamCel({ account }: { account: Account }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="truncate font-medium">{account.displayName}</span>
      <span
        className="truncate text-xs text-muted-foreground"
        title={account.email ?? undefined}
      >
        {account.email}
      </span>
    </div>
  );
}

/** What vouched for an unlinked account - the aanbieder, with the subject under it. The claimed
 *  naam is the obvious thing to put here and that is exactly why it may not be: on an open door
 *  whoever signed in chose it, and a column a beheerder scans down reads as identity. It stays
 *  in the detail pane, as a quotation. See ADR-0018, and the old Koppelingen list. */
function OngekoppeldNaamCel({
  account,
  t,
}: {
  account: Account;
  t: TFunction<"lokaleAccounts">;
}) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="truncate font-medium">
        {account.provider ?? accountDeurLabel(account.door)}
      </span>
      <span
        className="truncate font-mono text-xs text-muted-foreground"
        title={account.subject ?? undefined}
      >
        {account.subject ?? t("lijst.geenGebruiker")}
      </span>
    </div>
  );
}

/** Which cell to show depends on whether anything has verified who this account is. */
function NaamCel({
  account,
  t,
}: {
  account: Account;
  t: TFunction<"lokaleAccounts">;
}) {
  return account.persoonId ? (
    <GekoppeldNaamCel account={account} />
  ) : (
    <OngekoppeldNaamCel account={account} t={t} />
  );
}

/**
 * The koppeling state lives under the status rather than in a fourth column: the list pane
 * already carries three and a column of its own would push it past its half of an iPad.
 * It is here at all so that the unfiltered list shows what the filter strip counts.
 */
function StatusCel({
  account,
  statusLabel,
  t,
}: {
  account: Account;
  statusLabel: string;
  t: TFunction<"lokaleAccounts">;
}) {
  const koppeling = koppelingLabel(account, t);

  return (
    <span className="flex flex-col">
      <span>{statusLabel}</span>
      {isOutstanding(account.inloglinkExpiresOn) && (
        <span className="text-xs text-muted-foreground">
          {t("lijst.inloglinkOpen")}
        </span>
      )}
      {koppeling && (
        <span className="text-xs text-brand-blue">{koppeling}</span>
      )}
    </span>
  );
}

const kolommen = (t: TFunction<"lokaleAccounts">) =>
  helper.columns([
    helper.accessor("displayName", {
      header: t("lijst.naam"),
      cell: (info) => <NaamCel account={info.row.original} t={t} />,
    }),
    // With the status beside it, who has no way in is readable rather than deduced.
    helper.accessor((account) => accountDeurLabel(account.door), {
      id: "deur",
      header: t("lijst.deur"),
    }),
    helper.accessor((account) => accountStatusLabel(account.status), {
      id: "status",
      header: t("lijst.status"),
      cell: (info) => (
        <StatusCel
          account={info.row.original}
          statusLabel={info.getValue()}
          t={t}
        />
      ),
    }),
  ]);

/** No CardHeader: the page title above both panes already names the screen, and with the
 *  create form in a modal there is no second list to tell this one apart from. */
function AccountLijst({
  page,
  isPending,
  columns,
  account,
  onKies,
  sorting,
  onSortingChange,
  search,
  onGlobalFilterChange,
  onPaginaChange,
  filter,
  t,
  descriptors,
  stand,
  onStand,
  exportLink,
}: {
  page: ReturnType<typeof useLokaleAccountsPaged>["data"];
  isPending: boolean;
  columns: ReturnType<typeof kolommen>;
  account: Account | undefined;
  onKies: (accountId?: string) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  search: string;
  onGlobalFilterChange: (value: string) => void;
  onPaginaChange: (pagina: number) => void;
  filter: AccountFilter;
  t: TFunction<["lokaleAccounts", "koppelingen"]>;
  descriptors: Filterdescriptor<Account>[];
  stand: Filterstand;
  onStand: (stand: Filterstand) => void;
  exportLink: string;
}) {
  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {isPending || !page ? (
          <DataTableSkeleton
            label={t("lokaleAccounts:lijst.label")}
            columns={columns}
            rows={4}
            search
          />
        ) : (
          <>
            <DataTable
              label={t("lokaleAccounts:lijst.label")}
              data={page.items}
              columns={columns}
              getRowId={(row) => row.id}
              selectedId={account?.id}
              onSelect={onKies}
              searchPlaceholder={t("lokaleAccounts:lijst.zoek")}
              empty={t(`lokaleAccounts:lijst.leeg.${filter}`)}
              exportHref={exportLink}
              serverDriven={{
                sorting,
                onSortingChange,
                globalFilter: search,
                onGlobalFilterChange,
              }}
              filters={{
                descriptors,
                stand,
                onStand,
                toonTelling: false,
              }}
            />
            <Pager
              page={page.page}
              pageSize={page.pageSize}
              totalCount={page.totalCount}
              onPageChange={onPaginaChange}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** The server ordering for the current sort column, or JBNL's own default when nothing is sorted
 *  yet - `status` has none, since it is derived per row. */
const sortParams = (sort: SortingState[number] | undefined) => ({
  sortBy: sort && SORT_BY[sort.id],
  sortDesc: sort?.desc ?? true,
});

/** Above the toolbar rather than under both panes, and a warning rather than a card: it is not a
 *  property of the account you have open, it is the standing reason a whole class of accounts can
 *  never link by itself - and there is something to do about it, in Portaal. */
function DuplicatenWaarschuwing({
  duplicaten,
}: {
  duplicaten: Duplicaat[] | undefined;
}) {
  if (!duplicaten || duplicaten.length === 0) return null;
  return <DubbeleAdressen duplicaten={duplicaten} />;
}

/** The chosen account's detail, or a prompt to choose one - the detail pane's only two states. */
function AccountDetailPane({
  account,
  onDeleted,
  t,
}: {
  account: Account | undefined;
  onDeleted: () => void;
  t: TFunction<["lokaleAccounts", "koppelingen"]>;
}) {
  if (!account) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            {t("lokaleAccounts:lijst.kies")}
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <AccountDetail key={account.id} account={account} onDeleted={onDeleted} />
  );
}

/**
 * Every account that can sign in, with both the inloglink and the link to a persoon on one screen.
 * The waiting set survives that merge as a filter, because ADR-0018 wants a flagged link checked.
 * See docs/adr/0018-identity-linking.md and docs/adr/0020-local-accounts.md.
 */
export function LokaleAccounts({
  accountId,
  onKies,
  filter,
  onFilterChange,
  deur,
  status,
  onFiltersChange,
  pagina,
  onPaginaChange,
}: {
  accountId: string | undefined;
  onKies: (accountId?: string) => void;
  filter: AccountFilter;
  onFilterChange: (filter: AccountFilter) => void;
  /** The list's own filters (#194), read from the URL by the page. */
  deur: string | undefined;
  status: string | undefined;
  onFiltersChange: (volgende: {
    deur: string | undefined;
    status: string | undefined;
  }) => void;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
}) {
  const { t } = useTranslation(["lokaleAccounts", "koppelingen"]);
  const { data: duplicaten } = useDubbeleAdressen();

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sortBy, sortDesc } = sortParams(sorting[0]);

  // Server-driven, so neither descriptor carries `past` and the facets show no counts: this
  // list is one page of an answer the server already narrowed (#194).
  const descriptors = useMemo<Filterdescriptor<Account>[]>(
    () => [
      {
        id: "deur",
        label: t("lokaleAccounts:lijst.filterDeur"),
        kind: "keuze",
        opties: DEUREN.map((waarde) => ({
          waarde,
          label: accountDeurLabel(waarde),
        })),
      },
      {
        id: "status",
        label: t("lokaleAccounts:lijst.filterStatus"),
        kind: "keuze",
        opties: STATUSSEN.map((waarde) => ({
          waarde,
          label: accountStatusLabel(waarde),
        })),
      },
    ],
    [t],
  );
  const stand: Filterstand = {
    ...(deur && { deur: { soort: "keuze", waarde: deur } }),
    ...(status && { status: { soort: "keuze", waarde: status } }),
  };

  const { data: page, isPending } = useLokaleAccountsPaged(
    filter,
    pagina,
    FULL_PAGE_SIZE,
    search,
    sortBy,
    sortDesc,
    deur,
    status,
  );
  // Finds the selected account on the current page - independent of the list's own unpaginated
  // read, since there no longer is one. A selection that has scrolled off the page (paged past,
  // newly outranked by a search, or filtered away) simply shows no detail pane, an accepted
  // consequence of real pagination rather than a bug to work around.
  const account = page?.items.find((a) => a.id === accountId);

  const columns = kolommen(t);

  return (
    <div className="space-y-8">
      <DuplicatenWaarschuwing duplicaten={duplicaten} />

      {/* The filter on the left where reading starts, aanmaken on the right where the eye leaves
          the strip - the Toolbar's own split between what qualifies the list and what acts on it. */}
      <Toolbar
        leading={<AccountFilters value={filter} onChange={onFilterChange} />}
      >
        <IssueLokaalAccount />
      </Toolbar>

      <ListDetail
        selected={Boolean(account)}
        backLabel={t("lokaleAccounts:lijst.terug")}
        onBack={() => onKies()}
        list={
          <AccountLijst
            exportLink={exportHref("/api/beheer/lokale-accounts/export", {
              query: {
                filter: filter === "alle" ? undefined : filter,
                deur,
                status,
                search,
                sortBy,
                sortDescending: sortDesc,
              },
            })}
            page={page}
            isPending={isPending}
            columns={columns}
            account={account}
            onKies={onKies}
            sorting={sorting}
            onSortingChange={setSorting}
            search={search}
            // A search narrows the set the server counts, so page 2 of the old set would
            // otherwise land past the end of the new, shorter one.
            onGlobalFilterChange={(value) => {
              setSearch(value);
              onPaginaChange(1);
            }}
            onPaginaChange={onPaginaChange}
            filter={filter}
            t={t}
            descriptors={descriptors}
            stand={stand}
            onStand={(nieuw) => {
              const volgende = (id: string) => {
                const waarde = nieuw[id];
                return waarde?.soort === "keuze"
                  ? waarde.waarde || undefined
                  : undefined;
              };
              onFiltersChange({
                deur: volgende("deur"),
                status: volgende("status"),
              });
            }}
          />
        }
        detail={
          <AccountDetailPane
            account={account}
            onDeleted={() => onKies()}
            t={t}
          />
        }
      />
    </div>
  );
}

/**
 * Above the toolbar rather than under both panes, and a warning rather than a card: it is not a
 * property of the account you have open, it is the standing reason a whole class of accounts can
 * never link by itself - and there is something to do about it, in Portaal. A card at the foot of
 * the page is read by whoever scrolls that far, which is nobody who came to fix one account.
 */
function DubbeleAdressen({ duplicaten }: { duplicaten: Duplicaat[] }) {
  const { t } = useTranslation("koppelingen");

  return (
    <AlertList
      tone="warning"
      className="mb-4"
      title={t("duplicaten.title", { count: duplicaten.length })}
      description={t("duplicaten.uitleg")}
    >
      {/* A list rather than a comma-separated run, the shape Wedstrijdboekjes and Reistijden use:
          this runs to dozens of addresses, and a paragraph that long is a wall nobody reads to
          the end of. */}
      <ul className="list-disc space-y-0.5 ps-5">
        {duplicaten.map((duplicaat) => (
          <li key={duplicaat.email}>
            <span className="font-mono">{duplicaat.email}</span> -{" "}
            {duplicaat.namen.join(", ")}
          </li>
        ))}
      </ul>
    </AlertList>
  );
}

/**
 * An unlinked account is titled "Account", never the naam it claims. The claimed display
 * name is the obvious heading and that is exactly why it may not be one: on an open door
 * whoever signed in chose it, and a heading reads as identity. Once a persoon is linked
 * there is a verified answer to who this is, and the name can be the title again.
 * See docs/adr/0018-identity-linking.md.
 */
function AccountDetailHeader({
  account,
  t,
}: {
  account: Account;
  t: TFunction<"lokaleAccounts">;
}) {
  const gekoppeld = Boolean(account.persoonId);

  return (
    <CardHeader>
      <CardTitle>
        {gekoppeld ? account.displayName : t("detail.accountTitel")}
      </CardTitle>
      {gekoppeld && (
        // Truncated, with the whole of it on hover: an address is one long unbroken token, and
        // left to itself it runs straight out of the card.
        <CardDescription
          className="truncate"
          title={account.email ?? undefined}
        >
          {account.email}
        </CardDescription>
      )}
    </CardHeader>
  );
}

/** Keyed on the id by its caller, so switching accounts empties the persoon zoeker with it. */
function AccountDetail({
  account,
  onDeleted,
}: {
  account: Account;
  onDeleted: () => void;
}) {
  const { t } = useTranslation("lokaleAccounts");
  const send = useSendInloglink();
  const revoke = useRevokeInloglinks();
  const reset = useResetCredential();

  const outstanding = isOutstanding(account.inloglinkExpiresOn);

  return (
    <Card>
      <AccountDetailHeader account={account} t={t} />
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">{t("detail.status")}</dt>
          <dd>{accountStatusLabel(account.status)}</dd>
          <dt className="text-muted-foreground">{t("detail.deur")}</dt>
          <dd>{accountDeurLabel(account.door)}</dd>
          {account.source && (
            <>
              <dt className="text-muted-foreground">{t("detail.adresUit")}</dt>
              <dd>{accountBronLabel(account.source)}</dd>
            </>
          )}
          {/* Asked at the moment a beheerder is about to fire another one. */}
          <dt className="text-muted-foreground">
            {t("detail.laatstVerstuurd")}
          </dt>
          <dd>{datum(account.inloglinkSentOn, t)}</dd>
          <dt className="text-muted-foreground">
            {t("detail.laatstGebruikt")}
          </dt>
          <dd>{datum(account.inloglinkUsedOn, t)}</dd>
        </dl>

        {/* Shown, never typed: an address a beheerder could choose would let them mail
            themselves a session as anybody. */}
        <p className="text-xs text-muted-foreground">
          {account.source === "vbs"
            ? t("detail.adresUitlegVbs")
            : t("detail.adresUitleg")}
        </p>

        <InloglinkStatusberichten
          account={account}
          outstanding={outstanding}
          send={send}
          revoke={revoke}
          reset={reset}
        />

        <InloglinkActies
          account={account}
          send={send}
          revoke={revoke}
          reset={reset}
          outstanding={outstanding}
        />

        <p className="text-xs text-muted-foreground">
          {t("detail.resetUitleg")}
        </p>

        <Koppeling account={account} />

        <Rights account={account} />

        <RemoveAccount account={account} onDeleted={onDeleted} />
      </CardContent>
    </Card>
  );
}

/**
 * False while `me` loads, so a read-only beheerder never sees the switch work. The flag means
 * nothing for a persoon Portaal does not make beheerder, so the switch stays but cannot move.
 */
function useCanChangeRights(account: Account) {
  const { data: me } = useMe();

  return me?.isReadOnly === false && account.persoonIsBeheerder;
}

function rightsNoteKey(isBeheerder: boolean, canChange: boolean) {
  if (!isBeheerder) return "rechten.geenBeheerder";

  return canChange ? "rechten.uitleg" : "rechten.geenRecht";
}

/** Shown for every linked account; the switch only moves for a beheerder (ADR-0078). */
function Rights({ account }: { account: Account }) {
  const { t } = useTranslation("lokaleAccounts");
  const canChange = useCanChangeRights(account);
  const setRights = useSetRights();
  const switchId = useId();

  if (!account.persoonId) return null;

  return (
    <section className="space-y-3 border-t pt-4 text-sm" data-testid="rechten">
      <h3 className="font-medium">{t("rechten.title")}</h3>
      <div className="flex items-center gap-3">
        <Switch
          id={switchId}
          checked={account.persoonHasWriteAccess}
          disabled={!canChange || setRights.isPending}
          onCheckedChange={(hasWriteAccess) =>
            setRights.mutate({ id: account.id, hasWriteAccess })
          }
        />
        <Label htmlFor={switchId}>{t("rechten.label")}</Label>
      </div>
      <p className="text-xs text-muted-foreground">
        {t(rightsNoteKey(account.persoonIsBeheerder, canChange))}
      </p>
      {setRights.error && (
        <p className="text-sm text-destructive">{setRights.error.message}</p>
      )}
    </section>
  );
}

function RemoveAccount({
  account,
  onDeleted,
}: {
  account: Account;
  onDeleted: () => void;
}) {
  const { t } = useTranslation("lokaleAccounts");
  const { data: me } = useMe();
  const remove = useDeleteAccount();

  // Shown but disabled for a read-only beheerder, so the action can be found and the reason read.
  const canRemove = me?.isReadOnly === false;

  return (
    <section className="space-y-2 border-t pt-4">
      <ConfirmDelete
        trigger={
          <Button variant="outline" disabled={!canRemove || remove.isPending}>
            {t("verwijderen.knop")}
          </Button>
        }
        title={t("verwijderen.vraag")}
        description={t("verwijderen.beschrijving")}
        confirmLabel={t("verwijderen.bevestig")}
        onConfirm={() => remove.mutate(account.id, { onSuccess: onDeleted })}
      />
      {!canRemove && (
        <p className="text-xs text-muted-foreground">
          {t("verwijderen.geenRecht")}
        </p>
      )}
      {remove.error && (
        <p className="text-sm text-destructive">{remove.error.message}</p>
      )}
    </section>
  );
}

function UitstaandBericht({
  account,
  outstanding,
  t,
}: {
  account: Account;
  outstanding: boolean;
  t: TFunction<"lokaleAccounts">;
}) {
  if (!outstanding) return null;
  return (
    <p className="rounded-md border border-brand-blue/40 bg-brand-blue/5 p-3 text-sm">
      {t("detail.uitstaand", { datum: datum(account.inloglinkExpiresOn, t) })}
    </p>
  );
}

/** Superseded the moment an inloglink is outstanding again - that message already covers it. */
function VerstuurdBericht({
  send,
  outstanding,
  t,
}: {
  send: ReturnType<typeof useSendInloglink>;
  outstanding: boolean;
  t: TFunction<"lokaleAccounts">;
}) {
  if (!send.isSuccess || outstanding) return null;
  return <p className="text-sm">{t("detail.verstuurd")}</p>;
}

function IngetrokkenBericht({
  revoke,
  t,
}: {
  revoke: ReturnType<typeof useRevokeInloglinks>;
  t: TFunction<"lokaleAccounts">;
}) {
  if (!revoke.isSuccess) return null;
  return <p className="text-sm">{t("detail.ingetrokken")}</p>;
}

function GeresetBericht({
  reset,
  t,
}: {
  reset: ReturnType<typeof useResetCredential>;
  t: TFunction<"lokaleAccounts">;
}) {
  if (!reset.isSuccess) return null;
  return <p className="text-sm">{t("detail.gereset")}</p>;
}

function InloglinkStatusberichten({
  account,
  outstanding,
  send,
  revoke,
  reset,
}: {
  account: Account;
  outstanding: boolean;
  send: ReturnType<typeof useSendInloglink>;
  revoke: ReturnType<typeof useRevokeInloglinks>;
  reset: ReturnType<typeof useResetCredential>;
}) {
  const { t } = useTranslation("lokaleAccounts");

  return (
    <>
      <UitstaandBericht account={account} outstanding={outstanding} t={t} />
      <VerstuurdBericht send={send} outstanding={outstanding} t={t} />
      <IngetrokkenBericht revoke={revoke} t={t} />
      <GeresetBericht reset={reset} t={t} />
    </>
  );
}

function InloglinkActies({
  account,
  send,
  revoke,
  reset,
  outstanding,
}: {
  account: Account;
  send: ReturnType<typeof useSendInloglink>;
  revoke: ReturnType<typeof useRevokeInloglinks>;
  reset: ReturnType<typeof useResetCredential>;
  outstanding: boolean;
}) {
  const { t } = useTranslation("lokaleAccounts");

  return (
    <FormActions>
      {account.status !== "geblokkeerd" && (
        <Button
          disabled={send.isPending}
          onClick={() => send.mutate(account.id)}
        >
          {t("detail.stuurInloglink")}
        </Button>
      )}
      {outstanding && (
        <Button
          variant="outline"
          disabled={revoke.isPending}
          onClick={() => revoke.mutate(account.id)}
        >
          {t("detail.trekIn")}
        </Button>
      )}
      {account.status !== "geblokkeerd" && (
        <Button
          variant="outline"
          disabled={reset.isPending}
          onClick={() => reset.mutate(account.id)}
        >
          {t("detail.resetCredential")}
        </Button>
      )}
    </FormActions>
  );
}

/**
 * Who this account is, and the one action that changes the answer. Below the inloglink actions
 * rather than above them, because the question a beheerder opens this card with is almost always
 * "why can this person not get in" - and only sometimes "is this the right person".
 */
function Koppeling({ account }: { account: Account }) {
  const { t } = useTranslation(["lokaleAccounts", "koppelingen"]);

  return (
    <section
      className="space-y-3 border-t pt-4 text-sm"
      data-testid="koppeling"
    >
      <h3 className="font-medium">{t("lokaleAccounts:koppeling.title")}</h3>

      <VouchedFor account={account} />

      {account.persoonId ? (
        <Gekoppeld account={account} persoonId={account.persoonId} />
      ) : (
        <Wachtend account={account} />
      )}
    </section>
  );
}

/** The aanbieder and subject a provider vouched for. Skipped entirely for an account issued by
 *  JBNL itself, which has no provider and nothing to quote. */
function AanbiederInfo({ account }: { account: Account }) {
  const { t } = useTranslation(["lokaleAccounts", "koppelingen"]);

  if (!account.provider) return null;
  return (
    <p>
      <span className="text-muted-foreground">
        {t("koppelingen:detail.aanbieder")}
      </span>{" "}
      <span className="font-medium">{account.provider}</span>
      {account.subject && (
        <>
          {" · "}
          <span className="text-muted-foreground">
            {t("koppelingen:detail.gebruiker")}
          </span>{" "}
          <span className="font-mono text-xs break-all" title={account.subject}>
            {account.subject}
          </span>
        </>
      )}
    </p>
  );
}

/** The claimed naam and address, as a quotation - only where nothing verified says who this is.
 * Beside a linked persoon the same two values are already the card's title and subtitle, and a
 * quotation of them would read as doubt about an answer we do have. */
function GeclaimdeIdentiteit({ account }: { account: Account }) {
  const { t } = useTranslation(["lokaleAccounts", "koppelingen"]);

  if (account.persoonId) return null;
  return (
    <blockquote className="border-l-2 pl-3 text-muted-foreground">
      {t("koppelingen:detail.zegt")}
      {account.displayName ?? t("koppelingen:detail.geenNaam")}
      {account.email && <> · {account.email}</>}
    </blockquote>
  );
}

/**
 * The three things the provider actually vouched for, and - as a quotation, never as a heading -
 * the two it did not. On an open door the display name and address are chosen by whoever signed
 * in, so presenting them as a title is what makes the wrong click feel right.
 */
function VouchedFor({ account }: { account: Account }) {
  const { t } = useTranslation(["lokaleAccounts", "koppelingen"]);

  return (
    <div className="space-y-1">
      <AanbiederInfo account={account} />
      <p className="text-muted-foreground">
        {t("koppelingen:detail.eersteAanmelding", {
          moment: datumTijd(account.firstSignInOn),
        })}
      </p>
      <GeclaimdeIdentiteit account={account} />
    </div>
  );
}

const isBusy = (koppel: ReturnType<typeof useKoppel>) =>
  koppel.isPending || koppel.isSuccess;

function OnderdruktWaarschuwing({
  linkSuppressed,
  t,
}: {
  linkSuppressed: boolean;
  t: TFunction<["koppelingen", "common"]>;
}) {
  if (!linkSuppressed) return null;
  return (
    <p className="text-destructive">{t("koppelingen:detail.onderdrukt")}</p>
  );
}

function KoppelKnop({
  account,
  koppel,
  gekozen,
  busy,
  t,
}: {
  account: Account;
  koppel: ReturnType<typeof useKoppel>;
  gekozen: string | null;
  busy: boolean;
  t: TFunction<["koppelingen", "common"]>;
}) {
  return (
    <FormActions>
      {koppel.error && (
        <p className="text-destructive">{koppel.error.message}</p>
      )}
      <Button
        disabled={!gekozen || busy}
        onClick={() =>
          gekozen && koppel.mutate({ userId: account.id, persoonId: gekozen })
        }
      >
        {koppel.isPending
          ? t("common:actions.busy")
          : t("koppelingen:detail.koppelen")}
      </Button>
    </FormActions>
  );
}

function Wachtend({ account }: { account: Account }) {
  const { t } = useTranslation(["koppelingen", "common"]);
  const koppel = useKoppel();
  const [term, setTerm] = useState("");
  const [gekozen, setGekozen] = useState<string | null>(null);
  const busy = isBusy(koppel);

  return (
    <div className="space-y-3" data-testid="wachtend-account">
      <OnderdruktWaarschuwing linkSuppressed={account.linkSuppressed} t={t} />

      <PersoonZoeker
        term={term}
        onZoek={(zoekterm) => {
          setTerm(zoekterm);
          // A changed search invalidates the choice made under the old one, which would
          // otherwise stay selected and invisible above the new results.
          setGekozen(null);
        }}
        gekozen={gekozen}
        onKies={setGekozen}
        disabled={busy}
      />

      <KoppelKnop
        account={account}
        koppel={koppel}
        gekozen={gekozen}
        busy={busy}
        t={t}
      />
    </div>
  );
}

function GedriftWaarschuwing({
  account,
  t,
}: {
  account: Account;
  t: TFunction<"koppelingen">;
}) {
  if (!account.linkDriftedOn) return null;
  return <p className="text-muted-foreground">{t("detail.gedrift")}</p>;
}

function GekoppeldAanRegel({
  account,
  t,
}: {
  account: Account;
  t: TFunction<"koppelingen">;
}) {
  return (
    <p>
      <Trans
        t={t}
        i18nKey="detail.gekoppeldAan"
        values={{ naam: account.persoonNaam ?? "" }}
        components={{ strong: <strong /> }}
      />
      {account.persoonEmail &&
        t("detail.portaalAdres", { email: account.persoonEmail })}
    </p>
  );
}

/** Behind a confirmation, which the old Koppelingen screen did not need: there you only ever
 *  arrived for an account that was already known to be wrong. On a card every account has, a
 *  click that permanently stops this one from ever auto-linking again - and can only be undone
 *  by correcting Portaal - has to be asked about first. */
function OntkoppelActie({
  account,
  persoonId,
  ontkoppel,
  t,
}: {
  account: Account;
  persoonId: string;
  ontkoppel: ReturnType<typeof useOntkoppel>;
  t: TFunction<"koppelingen">;
}) {
  return (
    <FormActions>
      {ontkoppel.error && (
        <p className="text-destructive">{ontkoppel.error.message}</p>
      )}
      <ConfirmDelete
        trigger={
          <Button variant="outline" disabled={ontkoppel.isPending}>
            {t("detail.ontkoppelen")}
          </Button>
        }
        title={t("detail.ontkoppelenTitle")}
        description={t("detail.ontkoppelenUitleg", {
          naam: account.persoonNaam ?? "",
        })}
        confirmLabel={t("detail.ontkoppelen")}
        onConfirm={() => ontkoppel.mutate(persoonId)}
      />
    </FormActions>
  );
}

function Gekoppeld({
  account,
  persoonId,
}: {
  account: Account;
  persoonId: string;
}) {
  const { t } = useTranslation("koppelingen");
  const ontkoppel = useOntkoppel();

  return (
    <div className="space-y-3">
      <GedriftWaarschuwing account={account} t={t} />
      <GekoppeldAanRegel account={account} t={t} />
      <OntkoppelActie
        account={account}
        persoonId={persoonId}
        ontkoppel={ontkoppel}
        t={t}
      />
    </div>
  );
}
